<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import configService from '@/services/config.service'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

definePage({
  meta: {
    name: 'config',
    subject: 'global.menu',
    action: 'config',
  },
})

const snackbar = useToast()
const selectedUnit = cookieRef('selectedUnit')

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)
const isConfirmDialogVisible = ref(false)

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  key: [],
  type: [],
  unit_id: [],
})

const onError = error => {
  if (error.data) {
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  { title: 'Key', key: 'key' },
  { title: 'Type', key: 'type' },
  { title: 'Actions', key: 'actions', sortable: false },
]

const {
  data: configData,
  isFetching,
  execute: fetchConfig,
} = await useApi(createUrl('/configurations/configs', {
  query: {
    paged: 1,
    q: debouncedSearch,
    search: {
      unit_id: selectedUnit,
    },
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const configs = computed(() => configData.value?.data)
const totalConfig = computed(() => configData.value?.meta?.total)

const configDeleteData = ref()

const deleteConfig = async (confirm, data) => {
  if (data?.id && confirm) {
    await configService.deleteConfig(data.id, () => {
      fetchConfig()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = data => {
  configDeleteData.value = data
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value) {
    configDeleteData.value = {}
  }
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div style="inline-size: 10rem;">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <VBtn
          v-if="$can('create', 'config.config_head')"
          variant="flat"
          :to="{ name: 'app-global-config-add' }"
          prepend-icon="tabler-plus"
        >
          Add New Config
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="configs"
        :items-length="totalConfig"
        :headers="headers"
        :loading="isFetching"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.key="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.key ?? '—' }}</span>
        </template>

        <template #item.type="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.type ?? '—' }}</span>
        </template>

        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'configuration.config_head')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn v-if="$can('update', 'configuration.config_head')">
            <RouterLink
              :to="{ name: 'app-global-config-tab', params: { id: item.id } }"
              class="font-weight-medium text-link"
            >
              <VIcon icon="tabler-edit" />
            </RouterLink>
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>

    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Config Deleted."
      confirm-title="Deleted!"
      :data="configDeleteData"
      @confirm="deleteConfig"
    />
  </section>
</template>

<style lang="css">
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
